/**
 * Routes the Atlas page reads and writes. Served routes are marked with where the back
 * end defines them; PROPOSED routes are not served yet, and the page shows its designed
 * empty state until they are.
 */
import type { ApiClient } from './client';
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import { canSee, viewForPath, type Role } from '../roles';
import type {
  AtlasAnswerLink,
  AtlasAnswerTurn,
  AtlasAutonomyLevel,
  AtlasAutonomyResponse,
  AtlasAutonomySetResult,
  AtlasBrief,
  AtlasContext,
  AtlasEvalsView,
  AtlasMemoryResult,
  AtlasToday,
  AtlasProposalDecision,
  AtlasProposalsResponse,
  KillSwitchState,
  ScribeNoteType,
  ScribeResidentsResponse,
  ScribeSessionResponse,
  ScribeSessionsResponse,
  ScribeSignResult,
  ScribeSpeaker,
  ScribeTypesResponse,
} from './atlas-types';

const A = AITHORA_PREFIX;

// ── The rail and the opening turn (v3/atlas/context.ts, v3/overview/brief.ts) ──

/** GET /atlas/context: role line, scope, connected systems, earlier questions, memory (served). */
export const atlasContext = route<AtlasContext>()('GET', `${A}/atlas/context`, { honest: true });
/** GET /atlas/brief?scope=: the opening turn, written for the role (served). */
export const atlasBrief = route<AtlasBrief, { scope?: string }>()('GET', `${A}/atlas/brief`, { honest: true });
/** GET /atlas/today: the panel's Today tab from the caller's own building and role (served). */
export const atlasToday = route<AtlasToday>()('GET', `${A}/atlas/today`, { honest: true });
/** POST /atlas/memory/:id/forget: it stops steering answers (kept, with your name, until restored). Served. */
export const atlasMemoryForget = route<AtlasMemoryResult>()('POST', `${A}/atlas/memory/:id/forget`, { honest: true });
/** POST /atlas/memory/:id/restore (served). */
export const atlasMemoryRestore = route<AtlasMemoryResult>()('POST', `${A}/atlas/memory/:id/restore`, { honest: true });

// ── Proposals (src/server.ts 1154-1378) ──────────────────────────────────────

/** GET /api/atlas/proposals: the propose, approve, act queue (a building sees its own). */
export const atlasProposals = route<AtlasProposalsResponse, { limit?: number; cursor?: string; facilities?: string }>()('GET', '/api/atlas/proposals');
/** POST /api/atlas/proposals/:id/approve: re-gates fresh; 409 / 422 answer with a reason. */
export const atlasProposalApprove = route<AtlasProposalDecision, void, { reason?: string }>()('POST', '/api/atlas/proposals/:id/approve', { acceptStatuses: [409, 410, 422] });
/** POST /api/atlas/proposals/:id/reject: a person's no; nothing runs. */
export const atlasProposalReject = route<AtlasProposalDecision, void, { reason?: string }>()('POST', '/api/atlas/proposals/:id/reject', { acceptStatuses: [409] });

// ── Autonomy (src/server.ts 934-993) ─────────────────────────────────────────

/** GET /api/atlas/autonomy: the ladder and the building's (or tenant's) current level. */
/** GET /api/atlas/autonomy: the caller's building, or for the team one building (facilityId) or the company default. */
export const atlasAutonomy = route<AtlasAutonomyResponse, { facilityId?: string }>()('GET', '/api/atlas/autonomy');
/** PUT /api/atlas/autonomy: Operator and Facility Admin, per building (a Facility Admin only their own;
 *  the team one building by facilityId, or the company default without it). FULL answers 202 pending a second operator. */
export const atlasAutonomySet = route<AtlasAutonomySetResult, void, { level: AtlasAutonomyLevel; reason?: string; facilityId?: string }>()('PUT', '/api/atlas/autonomy');

// ── Freeze ───────────────────────────────────────────────────────────────────

/** GET /api/guardian/kill-switch/state: whether Atlas and Atlas are frozen. */
export const killSwitchState = route<KillSwitchState>()('GET', '/api/guardian/kill-switch/state');
/** POST /api/guardian/kill-switch: engage (Aithora) or release (the owner only; the server checks). */
export const killSwitchSet = route<KillSwitchState & { ok?: boolean }, void, { action: 'engage' | 'release'; scope: 'org' | 'global'; reason?: string; confirm: true }>()('POST', '/api/guardian/kill-switch');
// A building's pause is aiPauseState / aiPauseSet (oversight-routes.ts).

// ── Eval (router.ts 13660) ───────────────────────────────────────────────────

/** GET /atlas/evals: the live de-identification and routing scorecard (Aithora and Facility Admin). */
export const atlasEvals = route<AtlasEvalsView>()('GET', `${A}/atlas/evals`);

// ── Scribe (v3/atlas/scribe.ts) ──────────────────────────────────────────────

export const scribeTypes = route<ScribeTypesResponse>()('GET', `${A}/scribe/types`, { honest: true });
/** GET /scribe/residents: who a note can be about, with recording consent on file (served). */
export const scribeResidents = route<ScribeResidentsResponse>()('GET', `${A}/scribe/residents`, { honest: true });
export const scribeSessions = route<ScribeSessionsResponse, { status?: 'draft' | 'signed'; facilities?: string }>()('GET', `${A}/scribe/sessions`, { honest: true });
export const scribeSession = route<ScribeSessionResponse>()('GET', `${A}/scribe/sessions/:id`, { honest: true });
/** POST /scribe/sessions: 409 no_recording_consent when the resident has none on file. */
export const scribeStart = route<ScribeSessionResponse, void, { noteType: ScribeNoteType; residentId?: string; taskRef?: string }>()('POST', `${A}/scribe/sessions`, { honest: true });
/** POST /scribe/sessions/:id/transcript: `speaker` labels every line in the call that carries none. */
export const scribeTranscript = route<ScribeSessionResponse, void, { lines: (string | { text: string; speaker?: ScribeSpeaker | null })[]; speaker?: ScribeSpeaker }>()('POST', `${A}/scribe/sessions/:id/transcript`, { honest: true });
/** POST /scribe/sessions/:id/draft: Atlas fills the fields from what was said (503 when the drafter is not live). */
export const scribeDraft = route<ScribeSessionResponse>()('POST', `${A}/scribe/sessions/:id/draft`, { honest: true });
export const scribeActionDecide = route<ScribeSessionResponse, void, { decision: 'keep' | 'drop'; targetUserId?: string }>()('POST', `${A}/scribe/sessions/:id/actions/:actionId`, { honest: true });
export const scribeSign = route<ScribeSignResult>()('POST', `${A}/scribe/sessions/:id/sign`, { honest: true });
/** POST /scribe/sessions/:id/discard: drop an unsigned draft (sealed to the ledger as discarded). Served. */
export const scribeDiscard = route<{ real: boolean; id: string; discarded: boolean; ledgerEntryId?: string; ledgerLabel?: string }>()('POST', `${A}/scribe/sessions/:id/discard`, { honest: true });

// ── Ask (POST /api/atlas/ask, SSE; src/server.ts 1586-1724) ──────────────────

/** An empty answer, filled in as the stream arrives. */
export function emptyAnswerTurn(): AtlasAnswerTurn {
  return {
    text: '', route: null, withheld: false, tools: [], retrieval: null, gate: null, regulationsCited: [], done: false,
    status: [], steps: null, evidence: null, assessment: null, sources: [], proposals: [], followUps: [], sealed: null, marks: null, links: [],
  };
}

/** Fold one server-sent event into the answer. Unknown events are ignored. */
export function applyAtlasEvent(turn: AtlasAnswerTurn, event: string, data: unknown): AtlasAnswerTurn {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  switch (event) {
    case 'answer-chunk': {
      const t = String(d.text ?? '');
      return { ...turn, text: d.safeFallback ? t : turn.text + t, withheld: turn.withheld || !!d.safeFallback };
    }
    case 'answer-draft': {
      const t = String(d.text ?? '');
      const call = typeof d.call === 'number' ? d.call : null;
      // The warm fallback the server sends in place of a draft that failed its screen.
      if (d.safeFallback) return { ...turn, draft: t, draftCall: call, draftWithheld: true };
      if (turn.draftWithheld) return turn;
      return { ...turn, draft: call === (turn.draftCall ?? null) ? (turn.draft ?? '') + t : t, draftCall: call };
    }
    case 'answer':
      return { ...turn, text: String(d.answer ?? turn.text), route: (d.route as AtlasAnswerTurn['route']) ?? turn.route, withheld: !!d.withheld, answered: true };
    case 'trace':
      return {
        ...turn,
        tools: Array.isArray(d.tools) ? (d.tools as AtlasAnswerTurn['tools']) : turn.tools,
        retrieval: (d.retrieval as AtlasAnswerTurn['retrieval']) ?? turn.retrieval,
        steps: Array.isArray(d.steps) ? (d.steps as AtlasAnswerTurn['steps']) : turn.steps,
      };
    case 'gate':
      return { ...turn, gate: d as unknown as AtlasAnswerTurn['gate'] };
    case 'citations':
      return { ...turn, regulationsCited: Array.isArray(d.regulationsCited) ? (d.regulationsCited as string[]) : [] };
    case 'status':
      return { ...turn, status: Array.isArray(d.steps) ? (d.steps as AtlasAnswerTurn['status']) : turn.status };
    case 'evidence':
      return { ...turn, evidence: d as unknown as AtlasAnswerTurn['evidence'] };
    case 'assessment':
      return { ...turn, assessment: d as unknown as AtlasAnswerTurn['assessment'] };
    case 'sources':
      return { ...turn, sources: Array.isArray(d.sources) ? (d.sources as AtlasAnswerTurn['sources']) : turn.sources };
    case 'links':
      return { ...turn, links: Array.isArray(d.links) ? (d.links as AtlasAnswerTurn['links']) : turn.links };
    case 'proposals':
      return { ...turn, proposals: Array.isArray(d.proposals) ? (d.proposals as AtlasAnswerTurn['proposals']) : turn.proposals };
    case 'marks':
      return { ...turn, marks: Array.isArray(d.marks) ? (d.marks as AtlasAnswerTurn['marks']) : turn.marks };
    case 'done':
      return {
        ...turn,
        done: true,
        withheld: turn.withheld || !!d.withheld,
        followUps: Array.isArray(d.followUps) ? (d.followUps as string[]) : turn.followUps,
        sealed: typeof d.sealed === 'string' ? d.sealed : turn.sealed,
      };
    default:
      return turn;
  }
}

/**
 * The console links under an answer that this role can open (the server already left out the
 * rest; this checks again with the console's own page table, so a link never leads to "no access").
 */
export function consoleAnswerLinks(turn: Pick<AtlasAnswerTurn, 'links'>, role: Role | null): { label: string; path: string; kind: AtlasAnswerLink['kind'] }[] {
  const out: { label: string; path: string; kind: AtlasAnswerLink['kind'] }[] = [];
  for (const l of turn.links ?? []) {
    if (!l || typeof l.console !== 'string' || !l.console.startsWith('/')) continue;
    const view = viewForPath(l.console);
    if (!view || !canSee(role, view)) continue;
    out.push({ label: String(l.label ?? ''), path: l.console, kind: l.kind });
  }
  return out;
}

/**
 * The words to show for an answer while it streams: the draft as it is written, then the
 * finished answer (which went through the whole output gate) once it arrives.
 */
export function atlasShownText(turn: AtlasAnswerTurn): string {
  if (turn.answered || turn.done || turn.withheld) return turn.text;
  if (turn.draftWithheld) return turn.draft ?? '';
  const draft = turn.draft ?? '';
  return draft.length > turn.text.length ? draft : turn.text;
}

/** Reads the event stream block by block: `push` takes text as it arrives, `end` the rest. */
function sseReader(onEvent: (event: string, data: unknown) => void) {
  let buf = '';
  const take = (block: string) => {
    let ev = 'message';
    const data: string[] = [];
    for (const line of block.split(/\r?\n/)) {
      if (line.startsWith('event:')) ev = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
    }
    if (!data.length) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(data.join('\n'));
    } catch {
      return; // not JSON
    }
    onEvent(ev, parsed);
  };
  return {
    push(text: string) {
      buf += text;
      let i: number;
      while ((i = buf.search(/\r?\n\r?\n/)) >= 0) {
        const block = buf.slice(0, i);
        buf = buf.slice(buf.slice(i).match(/^\r?\n\r?\n/)![0].length + i);
        take(block);
      }
    },
    end() {
      if (buf.trim()) take(buf);
      buf = '';
    },
  };
}

/** POST /api/atlas/ask over XMLHttpRequest, read as it arrives (the phone). */
function atlasAskXhr(
  client: ApiClient,
  body: { question: string; history?: { role: 'user' | 'assistant'; content: string }[]; scope?: string | null },
  opts: { onTurn?: (turn: AtlasAnswerTurn) => void; signal?: AbortSignal },
): Promise<AtlasAnswerTurn> {
  return new Promise<AtlasAnswerTurn>((resolve, reject) => {
    let headers: Record<string, string>;
    try {
      headers = client.rawHeaders('/api/atlas/ask', 'POST', { 'Content-Type': 'application/json', Accept: 'text/event-stream' });
    } catch (e) {
      reject(e);
      return;
    }
    const xhr = new XMLHttpRequest();
    let turn = emptyAnswerTurn();
    let failure: string | null = null;
    let seen = 0;
    const reader = sseReader((ev, parsed) => {
      if (ev === 'error') {
        failure = String((parsed as { error?: unknown }).error ?? 'Atlas stopped part way.');
        return;
      }
      turn = applyAtlasEvent(turn, ev, parsed);
      opts.onTurn?.(turn);
    });
    const read = () => {
      if (xhr.status < 200 || xhr.status >= 300) return;
      const all = xhr.responseText ?? '';
      if (all.length > seen) {
        reader.push(all.slice(seen));
        seen = all.length;
      }
    };
    xhr.open('POST', client.url('/api/atlas/ask'));
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.onprogress = read;
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 3) read();
      if (xhr.readyState !== 4) return;
      const requestId = xhr.getResponseHeader('x-request-id');
      if (xhr.status === 0) {
        reject(new AtlasAskError('Could not reach Aithora. Check the connection and try again.', 0, requestId));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        let msg = `Atlas could not answer (${xhr.status}).`;
        try {
          const j = JSON.parse(xhr.responseText) as { error?: string };
          if (j?.error) msg = j.error;
        } catch {
          /* not JSON */
        }
        reject(new AtlasAskError(msg, xhr.status, requestId));
        return;
      }
      read();
      reader.end();
      if (failure && !turn.text) {
        reject(new AtlasAskError(failure, 200, requestId));
        return;
      }
      resolve({ ...turn, done: true });
    };
    opts.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(JSON.stringify(body));
  });
}

export class AtlasAskError extends Error {
  constructor(message: string, readonly status: number, readonly requestId: string | null) {
    super(message);
  }
}

/**
 * POST /api/atlas/ask, read as it streams: every event is folded into the answer and
 * handed to `onTurn` as it arrives. Resolves with the finished answer.
 */
export async function atlasAskStream(
  client: ApiClient,
  body: { question: string; history?: { role: 'user' | 'assistant'; content: string }[]; scope?: string | null },
  opts: {
    onTurn?: (turn: AtlasAnswerTurn) => void;
    signal?: AbortSignal;
    /**
     * 'xhr' reads the stream through XMLHttpRequest. React Native's fetch hands back the whole
     * body only when the answer is finished, so the phone uses this to show the answer as it
     * is written. Default: fetch with a streaming body reader (the browser).
     */
    transport?: 'fetch' | 'xhr';
  } = {},
): Promise<AtlasAnswerTurn> {
  if (opts.transport === 'xhr' && typeof XMLHttpRequest !== 'undefined') return atlasAskXhr(client, body, opts);
  const res = await client.raw('/api/atlas/ask', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(body),
    signal: opts.signal,
  });
  if (!res.ok) {
    let msg = `Atlas could not answer (${res.status}).`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch {
      /* not JSON */
    }
    throw new AtlasAskError(msg, res.status, res.headers.get('x-request-id'));
  }
  let turn = emptyAnswerTurn();
  let failure: string | null = null;
  const take = (block: string) => {
    let ev = 'message';
    const data: string[] = [];
    for (const line of block.split(/\r?\n/)) {
      if (line.startsWith('event:')) ev = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
    }
    if (!data.length) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(data.join('\n'));
    } catch {
      return;
    }
    if (ev === 'error') {
      failure = String((parsed as { error?: unknown }).error ?? 'Atlas stopped part way.');
      return;
    }
    turn = applyAtlasEvent(turn, ev, parsed);
    opts.onTurn?.(turn);
  };
  const reader = res.body?.getReader();
  if (reader) {
    const dec = new TextDecoder();
    let buf = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let i: number;
      while ((i = buf.search(/\r?\n\r?\n/)) >= 0) {
        const block = buf.slice(0, i);
        buf = buf.slice(buf.slice(i).match(/^\r?\n\r?\n/)![0].length + i);
        take(block);
      }
    }
    if (buf.trim()) take(buf);
  } else {
    for (const block of (await res.text()).split(/\r?\n\r?\n/)) take(block);
  }
  if (failure && !turn.text) throw new AtlasAskError(failure, 200, res.headers.get('x-request-id'));
  return { ...turn, done: true };
}
